import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { DatePicker, Form, Input, Switch, Spin, message, Divider } from "antd";
import dayjs from "dayjs";
import useApi from "../../hooks/useApi";
import { API_METHODS } from "../../constants";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";

const ADDON_PRICE = 300;
const PRASAD_PRICE = 100;
const SLOTS = [
  { value: "05:00", label: "5:00 AM", subtitle: "Morning" },
  { value: "07:30", label: "7:30 AM", subtitle: "Mid Morning" },
  { value: "10:00", label: "10:00 AM", subtitle: "Late Morning" },
];
const ADDONS = [
  { type: "naivedya", label: "Naivedya" },
  { type: "bhojan_prasad", label: "Bhojan Prasad" },
];
const WHATS_INCLUDED = [
  "Performed at Shri Mahalaxmi Mandir, Kolhapur",
  "Hereditary Shreepujak",
  "Naivedya & Bhojan Prasad available",
  "Prasad by courier on request",
];
const TRUST_BADGES = [
  { icon: "🛡️", label: "100% Authentic" },
  { icon: "⚡", label: "Instant Booking" },
];

function QtyControl({ value, onChange, min = 0 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <button type="button" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}
        style={{ width: 34, height: 34, borderRadius: 8, border: "1.5px solid #f97316", background: "white", color: "#f97316", fontSize: 18, fontWeight: 700, cursor: value <= min ? "not-allowed" : "pointer", opacity: value <= min ? 0.35 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
      <span style={{ width: 22, textAlign: "center", fontWeight: 700, fontSize: 15 }}>{value}</span>
      <button type="button" onClick={() => onChange(value + 1)}
        style={{ width: 34, height: 34, borderRadius: 8, border: "1.5px solid #f97316", background: "white", color: "#f97316", fontSize: 18, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
    </div>
  );
}

function SectionCard({ title, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ background: "white", borderRadius: 16, border: "1px solid #f3e8d8", padding: "20px 22px", marginBottom: 16, boxShadow: hovered ? "0 6px 24px rgba(249,115,22,0.13)" : "0 2px 10px rgba(0,0,0,0.05)", transform: hovered ? "translateY(-1px)" : "none", transition: "all 0.18s" }}>
      {title && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <div style={{ width: 3, height: 18, background: "linear-gradient(to bottom, #f97316, #dc2626)", borderRadius: 4 }} />
          <span style={{ fontWeight: 700, fontSize: 12, color: "#9a3412", letterSpacing: "0.07em", textTransform: "uppercase" }}>{title}</span>
        </div>
      )}
      {children}
    </div>
  );
}

export default function BookingPage() {
  const { serviceSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const [serviceResponse, fetchService] = useApi(null, null, API_METHODS.GET);
  useEffect(() => { fetchService(null, API_METHODS.GET, `/user/services/${serviceSlug}`); }, [serviceSlug]);
  const service = serviceResponse?.data || null;

  const [qty, setQty] = useState(1);
  const [addonQtys, setAddonQtys] = useState({ naivedya: 0, bhojan_prasad: 0 });
  const [prasad, setPrasad] = useState(false);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [paying, setPaying] = useState(false);

  // Prefill from logged-in user (login is optional for booking)
  useEffect(() => {
    try {
      const raw = localStorage.getItem("user");
      if (!localStorage.getItem("authToken") || !raw) return;
      const u = JSON.parse(raw);
      const phone = String(u?.phone || "").replace(/\D/g, "").slice(-10);
      const patch = {};
      if (phone.length === 10) patch.whatsapp = phone;
      if (u?.name && u.name !== u.phone) patch.name = u.name;
      if (Object.keys(patch).length) form.setFieldsValue(patch);
    } catch (e) {
      /* ignore malformed localStorage */
    }
  }, []);

  const [bookingResponse, createBooking] = useApi(null, null, API_METHODS.POST);
  const [verifyResponse, verifyPayment] = useApi(null, null, API_METHODS.POST);

  const isCustomPricing = service?.price_type === "custom" || service?.price_type === "minimum";
  const customAmount = parseFloat(searchParams.get("amount"));
  const servicePrice = isCustomPricing && customAmount > 0 ? customAmount : service?.price || 0;
  const serviceTotal = servicePrice * qty;
  const addonTotal = ADDONS.reduce((s, a) => s + addonQtys[a.type] * ADDON_PRICE, 0);
  const prasadTotal = prasad ? PRASAD_PRICE : 0;
  const grandTotal = serviceTotal + addonTotal + prasadTotal;
  const activeAddons = ADDONS.filter((a) => addonQtys[a.type] > 0);

  const disabledDate = (cur) => cur && cur <= dayjs().endOf("day");
  // Services can opt out of fixed puja slots; default to required when flag absent
  const slotRequired = service ? !!service.slot_required : true;

  const handlePay = () => {
    form.validateFields().then(async (values) => {
      if (!date) { messageApi.error("Please select a date"); return; }
      if (slotRequired && !slot) { messageApi.error("Please select a time slot"); return; }
      if (isCustomPricing && !(customAmount > 0)) { messageApi.error("Invalid amount. Please go back and enter an amount."); return; }
      setPaying(true);
      const loaded = await new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true); s.onerror = () => resolve(false);
        document.body.appendChild(s);
      });
      if (!loaded) { messageApi.error("Razorpay failed to load."); setPaying(false); return; }
      const addons = ADDONS.filter((a) => addonQtys[a.type] > 0).map((a) => ({ type: a.type, qty: addonQtys[a.type] }));
      await createBooking({
        service_id: service.id, quantity: qty,
        service_date: date.format("YYYY-MM-DD"),
        name: values.name.trim(), gotra: values.gotra?.trim() || undefined,
        whatsapp_number: values.whatsapp.trim(), slot: slotRequired ? slot : undefined, addons,
        prasad_courier: prasad,
        ...(isCustomPricing && customAmount > 0 && { custom_price: customAmount }),
        ...(prasad && {
          prasad_name: values.prasad_name.trim(),
          prasad_address_line1: values.prasad_line1.trim(),
          prasad_address_line2: values.prasad_line2?.trim() || undefined,
          prasad_city: values.prasad_city.trim(), prasad_state: values.prasad_state.trim(),
          prasad_pincode: values.prasad_pincode.trim(),
        }),
      }, API_METHODS.POST, "/user/orders/book-service");
    }).catch(() => messageApi.error("Please fill in all required fields"));
  };

  useEffect(() => {
    if (!bookingResponse || bookingResponse.isLoading) return;
    if (bookingResponse.isError) { setPaying(false); return; }
    const data = bookingResponse.data;
    if (!data?.razorpay_order_id) return;
    new window.Razorpay({
      key: data.key, amount: data.amount * 100, currency: data.currency,
      name: "MahalaxmiPuja", description: service?.name || "Puja Booking",
      order_id: data.razorpay_order_id, theme: { color: "#f97316" },
      prefill: { name: form.getFieldValue("name") || "", contact: form.getFieldValue("whatsapp") || "" },
      handler: async (response) => {
        await verifyPayment({ order_id: data.order_id, razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature }, API_METHODS.POST, "/user/orders/book-service/verify");
      },
      modal: { ondismiss: () => { messageApi.warning("Payment cancelled."); setPaying(false); } },
    }).open();
  }, [bookingResponse]);

  useEffect(() => {
    if (!verifyResponse || verifyResponse.isLoading) return;
    if (verifyResponse.isError) { setPaying(false); return; }
    if (verifyResponse.data?.payment_status === "completed") {
      navigate("/booking/success", { state: { order_number: verifyResponse.data.order_number, service_name: service?.name, booking_date: date?.format("YYYY-MM-DD"), slot: slotRequired ? slot : null } });
    }
  }, [verifyResponse]);

  if (serviceResponse?.isLoading || (!service && !serviceResponse?.isError)) {
    return <div style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}><Spin size="large" /></div>;
  }
  if (!service) {
    return <div style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>Service not found.</div>;
  }

  return (
    <div style={{ minHeight: "100vh", background: "#faf7f4" }}>
      {contextHolder}
      <HeaderComponent />

      {/* Hero */}
      <div style={{ position: "relative", height: 280, overflow: "hidden" }}>
        {service.image_url
          ? <img src={service.image_url} alt={service.name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
          : <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #f97316, #dc2626)" }} />}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.60) 100%)" }} />
        <div style={{ position: "relative", zIndex: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: "0 32px 28px", maxWidth: 1200, margin: "0 auto" }}>
          <button onClick={() => navigate("/services")} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 999, padding: "5px 14px", color: "white", fontSize: 13, fontWeight: 500, cursor: "pointer", marginBottom: 12, width: "fit-content", backdropFilter: "blur(8px)" }}>
            ← Back to Services
          </button>
          {service.service_type_name && (
            <div style={{ display: "inline-block", background: "rgba(249,115,22,0.85)", borderRadius: 999, padding: "3px 12px", fontSize: 11, fontWeight: 700, color: "white", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10, width: "fit-content" }}>
              {service.service_type_name}
            </div>
          )}
          <h1 style={{ fontSize: 36, fontWeight: 800, color: "white", margin: "0 0 8px", textShadow: "0 2px 12px rgba(0,0,0,0.3)", lineHeight: 1.15 }}>
            {service.name}
          </h1>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 14, display: "flex", alignItems: "center", gap: 5 }}>
              📍 Shri Mahalaxmi Mandir, Kolhapur
            </span>
            <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 14, display: "flex", alignItems: "center", gap: 4 }}>
              ⭐ 4.8 <span style={{ opacity: 0.7 }}>(360+ devotees)</span>
            </span>
          </div>
        </div>
      </div>

      {/* Two-column body */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 20px 64px", display: "flex", gap: 28, alignItems: "flex-start" }}>

        {/* LEFT — service info */}
        <div style={{ flex: "1 1 0", minWidth: 0 }}>

          {/* About */}
          {service.description && (
            <SectionCard title="About this Service">
              <p style={{ color: "#4b5563", lineHeight: 1.75, margin: 0, fontSize: 15 }}>{service.description}</p>
            </SectionCard>
          )}

          {/* Trust badges */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, marginBottom: 16 }}>
            {TRUST_BADGES.map((b) => (
              <div key={b.label} style={{ background: "white", border: "1px solid #f3e8d8", borderRadius: 14, padding: "18px 12px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>{b.icon}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{b.label}</div>
              </div>
            ))}
          </div>

          {/* What's included */}
          <SectionCard title="What's Included">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {WHATS_INCLUDED.map((item) => (
                <div key={item} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#fff7ed", border: "1.5px solid #f97316", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <svg width="11" height="11" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" /></svg>
                  </div>
                  <span style={{ fontSize: 14, color: "#374151" }}>{item}</span>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Temple info */}
          <div style={{ background: "white", border: "1px solid #f3e8d8", borderRadius: 16, padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg, #f97316, #dc2626)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>🛕</div>
            <div>
              <div style={{ fontWeight: 700, color: "#1f2937", fontSize: 15 }}>Shri Mahalaxmi Mandir, Kolhapur</div>
            </div>
          </div>
        </div>

        {/* RIGHT — sticky booking form */}
        <div style={{ width: 400, flexShrink: 0, position: "sticky", top: 88 }}>

          {/* Price card header */}
          <div style={{ background: "linear-gradient(135deg, #f97316, #dc2626)", borderRadius: "16px 16px 0 0", padding: "20px 24px" }}>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Service Fee</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <span style={{ fontSize: 36, fontWeight: 800, color: "white" }}>₹{servicePrice.toLocaleString("en-IN")}</span>
              <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>/ booking</span>
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", marginTop: 4 }}>⭐ 4.8 rating · 360+ confirmed bookings</div>
          </div>

          {/* Form body */}
          <div style={{ background: "white", borderRadius: "0 0 16px 16px", border: "1px solid #f3e8d8", borderTop: "none", padding: "20px 20px 24px", boxShadow: "0 8px 32px rgba(0,0,0,0.08)" }}>
            <Form form={form} layout="vertical" size="middle" requiredMark={false}>

              {/* Quantity */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9a3412", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 10 }}>Quantity</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fafafa", borderRadius: 10, padding: "10px 14px", border: "1px solid #f3e8d8" }}>
                  <div>
                    <div style={{ fontWeight: 600, color: "#374151", fontSize: 14 }}>{service.name}</div>
                    <div style={{ fontSize: 12, color: "#9ca3af" }}>₹{servicePrice.toLocaleString("en-IN")} each</div>
                  </div>
                  <QtyControl value={qty} onChange={setQty} min={1} />
                </div>
              </div>

              {/* Add-ons */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9a3412", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 10 }}>Add-ons — ₹300 each</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {ADDONS.map((addon) => (
                    <div key={addon.type} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fafafa", borderRadius: 10, padding: "10px 14px", border: "1px solid #f3e8d8" }}>
                      <span style={{ fontWeight: 500, color: "#374151", fontSize: 14 }}>{addon.label}</span>
                      <QtyControl value={addonQtys[addon.type]} onChange={(v) => setAddonQtys((p) => ({ ...p, [addon.type]: v }))} min={0} />
                    </div>
                  ))}
                </div>
              </div>

              <Divider style={{ margin: "8px 0 14px" }} />

              {/* Your Details */}
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9a3412", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 10 }}>Your Details</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                <Form.Item label={<span style={{ fontWeight: 500, color: "#374151", fontSize: 13 }}>Name *</span>} name="name" rules={[{ required: true, message: "Required" }]} style={{ gridColumn: "1/2" }}>
                  <Input placeholder="Full name" style={{ borderRadius: 8 }} />
                </Form.Item>
                <Form.Item label={<span style={{ fontWeight: 500, color: "#374151", fontSize: 13 }}>Gotra</span>} name="gotra" style={{ gridColumn: "2/3" }}>
                  <Input placeholder="Optional" style={{ borderRadius: 8 }} />
                </Form.Item>
                <Form.Item label={<span style={{ fontWeight: 500, color: "#374151", fontSize: 13 }}>WhatsApp *</span>} name="whatsapp"
                  rules={[{ required: true, message: "Required" }, { validator: (_, v) => /^\d{10}$/.test(String(v || "").replace(/\D/g, "")) ? Promise.resolve() : Promise.reject(new Error("10-digit number")) }]}
                  style={{ gridColumn: "1/3" }}>
                  <Input placeholder="10-digit WhatsApp number" inputMode="numeric" maxLength={10} style={{ borderRadius: 8 }} onChange={(e) => form.setFieldValue("whatsapp", e.target.value.replace(/\D/g, ""))} />
                </Form.Item>
              </div>

              {/* Prasad toggle */}
              <div style={{ background: "#fff7ed", border: "1px solid #ffedd5", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 600, color: "#1f2937", fontSize: 14 }}>Prasad by Courier</div>
                    <div style={{ fontSize: 12, color: "#f97316", fontWeight: 500 }}>+ ₹100 delivery charge</div>
                  </div>
                  <Switch checked={prasad} onChange={(val) => { setPrasad(val); if (val) form.setFieldValue("prasad_name", form.getFieldValue("name") || ""); }} style={{ backgroundColor: prasad ? "#f97316" : "#d1d5db" }} />
                </div>
                {prasad && (
                  <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                    <Form.Item label={<span style={{ fontSize: 12, fontWeight: 500 }}>Recipient *</span>} name="prasad_name" rules={[{ required: true, message: "Required" }]} style={{ gridColumn: "1/3" }}>
                      <Input placeholder="Name for delivery" style={{ borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item label={<span style={{ fontSize: 12, fontWeight: 500 }}>Address Line 1 *</span>} name="prasad_line1" rules={[{ required: true, message: "Required" }]} style={{ gridColumn: "1/3" }}>
                      <Input placeholder="House / Flat / Building" style={{ borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item label={<span style={{ fontSize: 12, fontWeight: 500 }}>Address Line 2</span>} name="prasad_line2" style={{ gridColumn: "1/3" }}>
                      <Input placeholder="Street / Area (optional)" style={{ borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item label={<span style={{ fontSize: 12, fontWeight: 500 }}>City *</span>} name="prasad_city" rules={[{ required: true, message: "Required" }]}>
                      <Input placeholder="City" style={{ borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item label={<span style={{ fontSize: 12, fontWeight: 500 }}>State *</span>} name="prasad_state" rules={[{ required: true, message: "Required" }]}>
                      <Input placeholder="State" style={{ borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item label={<span style={{ fontSize: 12, fontWeight: 500 }}>Pincode *</span>} name="prasad_pincode" rules={[{ required: true, message: "Required" }, { pattern: /^\d{6}$/, message: "6-digit" }]} style={{ gridColumn: "1/2" }}>
                      <Input placeholder="Pincode" inputMode="numeric" maxLength={6} style={{ borderRadius: 8 }} onChange={(e) => form.setFieldValue("prasad_pincode", e.target.value.replace(/\D/g, ""))} />
                    </Form.Item>
                  </div>
                )}
              </div>

              {/* Date */}
              <div style={{ fontSize: 11, fontWeight: 700, color: "#9a3412", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>Select Date</div>
              <DatePicker style={{ width: "100%", borderRadius: 8, marginBottom: 14 }} disabledDate={disabledDate} value={date} onChange={setDate} placeholder="Select puja date" format="DD MMM YYYY" />

              {/* Slot — only for services that need a fixed slot */}
              {slotRequired && (
                <React.Fragment>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9a3412", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>Time Slot</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
                    {SLOTS.map((s) => {
                      const selected = slot === s.value;
                      return (
                        <button key={s.value} type="button" onClick={() => setSlot(s.value)}
                          style={{ padding: "10px 4px", borderRadius: 10, border: selected ? "2px solid transparent" : "2px solid #e5e7eb", background: selected ? "linear-gradient(135deg, #f97316, #dc2626)" : "white", color: selected ? "white" : "#374151", fontWeight: 700, fontSize: 12, cursor: "pointer", transition: "all 0.15s", boxShadow: selected ? "0 3px 10px rgba(249,115,22,0.40)" : "none", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                          <span>{s.label}</span>
                          <span style={{ fontSize: 10, fontWeight: 500, opacity: selected ? 0.85 : 0.45 }}>{s.subtitle}</span>
                        </button>
                      );
                    })}
                  </div>
                </React.Fragment>
              )}

              {/* Price summary */}
              <div style={{ background: "#fafafa", border: "1px solid #f3e8d8", borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280", fontSize: 13 }}>
                    <span>{service.name} × {qty}</span>
                    <span>₹{serviceTotal.toLocaleString("en-IN")}</span>
                  </div>
                  {activeAddons.map((a) => (
                    <div key={a.type} style={{ display: "flex", justifyContent: "space-between", color: "#6b7280", fontSize: 13 }}>
                      <span>{a.label} × {addonQtys[a.type]}</span>
                      <span>₹{(addonQtys[a.type] * ADDON_PRICE).toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                  {prasad && (
                    <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280", fontSize: 13 }}>
                      <span>Prasad courier</span><span>₹{PRASAD_PRICE}</span>
                    </div>
                  )}
                  <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 8, marginTop: 2, display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 16 }}>
                    <span style={{ color: "#1f2937" }}>Total</span>
                    <span style={{ background: "linear-gradient(to right, #f97316, #dc2626)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                      ₹{grandTotal.toLocaleString("en-IN")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Pay button */}
              <button type="button" disabled={paying} onClick={handlePay}
                style={{ width: "100%", height: 52, background: paying ? "#d1d5db" : "linear-gradient(to right, #f97316, #dc2626)", border: "none", borderRadius: 14, color: "white", fontSize: 16, fontWeight: 800, cursor: paying ? "not-allowed" : "pointer", boxShadow: paying ? "none" : "0 4px 18px rgba(249,115,22,0.45)", animation: paying ? "none" : "payGlow 2s ease-in-out infinite", letterSpacing: "0.02em" }}>
                {paying ? "Processing…" : `Book Now — ₹${grandTotal.toLocaleString("en-IN")}`}
              </button>

              {/* Trust strip */}
              <div style={{ textAlign: "center", marginTop: 12, color: "#9ca3af", fontSize: 11 }}>
                🔒 Secure Payment &nbsp;·&nbsp; ⚡ Instant Confirmation
              </div>

            </Form>
          </div>
        </div>

      </div>

      <Footer />

      <style>{`
        @keyframes payGlow {
          0%, 100% { box-shadow: 0 4px 18px rgba(249,115,22,0.45); }
          50% { box-shadow: 0 6px 28px rgba(249,115,22,0.68); }
        }
        @media (max-width: 768px) {
          .booking-two-col { flex-direction: column !important; }
          .booking-sidebar { width: 100% !important; position: static !important; }
        }
      `}</style>
    </div>
  );
}
